import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { form, FormField, submit } from '@angular/forms/signals';
import { FormValidationService, UIService } from '@core/services';
import { TranslateModule } from '@ngx-translate/core';
import { DesignSystemModule } from '@paypoint-frontend/design-system';
import { SpinnerComponent } from '../../../../shared/spinner/spinner.component';
import { ApiService } from '../api.service';
import { CashoutOperation, CashoutOperationStatus } from '../model';
import { Service as EcomCashoutService } from '../service';
import { mapBootstrapParams, mapSetecomPageModel } from './adapter';
import { FormSchema } from './form-shema';
import { EcomCashoutSetecomMapService } from './map.service';

@Component({
  selector: 'app-ecom-cashout-setecom',
  templateUrl: './component.html',
  styleUrl: './component.scss',
  imports: [
    CommonModule,
    TranslateModule,
    DesignSystemModule,
    FormField,
    SpinnerComponent,
  ],
  providers: [
    ApiService,
    EcomCashoutService,
    EcomCashoutSetecomMapService,
    FormSchema,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EcomCashoutSetecomComponent implements AfterViewInit {
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly service = inject(EcomCashoutService);
  private readonly mapService = inject(EcomCashoutSetecomMapService);
  private readonly ui = inject(UIService);
  private readonly formSchema = inject(FormSchema);
  private readonly operation = signal<CashoutOperation | null>(null);
  private readonly hasApiEmail = signal(false);
  private readonly isEmailSent = signal(false);
  private copyFeedbackTimeout: number | null = null;
  private requestedOperationKey: string | null = null;
  private initializedEmailOperationId: number | null = null;
  protected readonly formvalidation = inject(FormValidationService);

  protected statusLabelKey(status: CashoutOperationStatus): string {
    switch (status) {
      case CashoutOperationStatus.PAYMENT_CREATED:
        return 'ECOMCASHOUT.SETECOM.ORDER.STATUS.PAYMENTCREATED';
      case CashoutOperationStatus.COMPLETED:
        return 'ECOMCASHOUT.SETECOM.ORDER.STATUS.COMPLETED';
      case CashoutOperationStatus.ERROR:
        return 'ECOMCASHOUT.SETECOM.ORDER.STATUS.ERROR';
      case CashoutOperationStatus.EXPIRED:
        return 'ECOMCASHOUT.SETECOM.ORDER.STATUS.EXPIRED';
      case CashoutOperationStatus.INITIATED:
      default:
        return 'ECOMCASHOUT.SETECOM.ORDER.STATUS.INITIATED';
    }
  }

  protected readonly isTransferNumberCopied = signal(false);
  protected readonly emailForm = form(
    this.formSchema.default,
    this.formSchema.schema,
  );

  readonly params = input<unknown | null>(null);
  protected readonly inProgress = computed(() => this.ui.inProgress());
  protected readonly pageModel = computed(() =>
    mapSetecomPageModel(this.params(), this.operation()),
  );
  protected readonly shouldShowResendEmail = computed(() => !this.hasApiEmail());
  protected readonly canResendEmail = computed(
    () =>
      this.shouldShowResendEmail() &&
      !this.isEmailSent() &&
      !this.inProgress() &&
      !this.emailForm().invalid(),
  );

  constructor() {
    effect(() => {
      const email = this.pageModel().customerEmail.trim();
      const operationId = this.operation()?.id ?? null;

      if (operationId !== null && this.initializedEmailOperationId !== operationId) {
        this.hasApiEmail.set(email.length > 0);
        this.isEmailSent.set(false);
        this.formSchema.emailDisabled.set(email.length > 0);
        this.emailForm.email().value.set(email);
        this.initializedEmailOperationId = operationId;
      }
    });

    effect(() => {
      const inputParams = this.params();

      if (inputParams === null) {
        return;
      }

      const { paymentId, signature } = mapBootstrapParams(inputParams);

      if (paymentId <= 0 || !signature) {
        return;
      }

      const requestKey = `${paymentId}:${signature}`;

      if (this.requestedOperationKey === requestKey) {
        return;
      }

      this.requestedOperationKey = requestKey;

      this.service.getOperation(paymentId, signature).subscribe({
        next: (operation) => {
          this.operation.set(operation);
        },
        error: () => window.location.assign('/error/pspexception'),
      });
    });
  }

  ngAfterViewInit(): void {
    const mapElement =
      this.elementRef.nativeElement.querySelector<HTMLElement>('#map');

    if (mapElement) {
      void this.mapService.load(mapElement);
    }
  }

  protected async copyTransferNumber(): Promise<void> {
    const value = this.pageModel().transferNumber;

    if (!value) {
      return;
    }

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(value);
      this.showCopyFeedback();
      return;
    }

    if (this.copyTransferNumberFallback(value)) {
      this.showCopyFeedback();
    }
  }

  private copyTransferNumberFallback(value: string): boolean {
    const document = this.elementRef.nativeElement.ownerDocument;
    const textarea = document.createElement('textarea');

    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    const copied = document.execCommand('copy');
    textarea.remove();

    return copied;
  }

  private showCopyFeedback(): void {
    this.isTransferNumberCopied.set(true);

    if (this.copyFeedbackTimeout !== null) {
      window.clearTimeout(this.copyFeedbackTimeout);
    }

    this.copyFeedbackTimeout = window.setTimeout(() => {
      this.isTransferNumberCopied.set(false);
      this.copyFeedbackTimeout = null;
    }, 2000);
  }

  protected backToMerchant(): void {
    const successUrl = this.pageModel().successUrl;

    if (successUrl) {
      window.location.assign(successUrl);
      return;
    }

    window.history.back();
  }

  protected resendEmail(event: Event): void {
    event.preventDefault();
    const params = mapBootstrapParams(this.params());
    const operationId = this.operation()?.id ?? params.paymentId;

    if (operationId <= 0 || !this.canResendEmail()) {
      return;
    }

    submit(this.emailForm, async () => {
      const emailAddress = this.emailForm.email().value().trim();

      this.service
        .resendEmail({
          operationId,
          email: emailAddress,
        })
        .subscribe({
          next: () => {
            this.isEmailSent.set(true);
            this.formSchema.emailDisabled.set(true);
          },
          error: () => {
            this.isEmailSent.set(true);
            this.formSchema.emailDisabled.set(true);
          },
        });
    });
  }
}
